/**
 * Entity Filtering Utilities
 *
 * Case-insensitive partial matching for pairs and projects.
 * Used by search feature to filter entities based on user queries.
 */

import type { EnrichedPair } from '../model/selectors';

/**
 * Filter pairs by search query
 * Matches against symbol, project name, platform, pairType, and IDs
 *
 * @param pairs - Array of enriched pairs
 * @param query - Search query string
 * @returns Filtered array of pairs matching the query
 */
export function filterPairsByQuery(pairs: EnrichedPair[], query: string): EnrichedPair[] {
  if (!pairs || pairs.length === 0) {
    return [];
  }

  if (!query || !query.trim()) {
    return [];
  }

  const trimmedQuery = query.trim();
  const lowerQuery = trimmedQuery.toLowerCase();

  return pairs.filter(pair => {
    // Match against symbol (e.g., "BTC/USDT")
    if (pair.symbol && pair.symbol.toLowerCase().includes(lowerQuery)) {
      return true;
    }

    // Match against individual tokens (base/quote)
    if (pair.baseToken && pair.baseToken.toLowerCase().includes(lowerQuery)) {
      return true;
    }
    if (pair.quoteToken && pair.quoteToken.toLowerCase().includes(lowerQuery)) {
      return true;
    }

    // Match against project name (e.g., "AriaAI")
    if (pair.projectName && pair.projectName.toLowerCase().includes(lowerQuery)) {
      return true;
    }

    // Match against platform (e.g., "binance", "uniswap")
    if (pair.platform && pair.platform.toLowerCase().includes(lowerQuery)) {
      return true;
    }

    // Match against pair type (cex, dex, sol)
    if (pair.pairType && pair.pairType.toLowerCase().includes(lowerQuery)) {
      return true;
    }

    // Match against pair ID
    if (String(pair.pairId).includes(trimmedQuery)) {
      return true;
    }

    // Match against project ID
    if (String(pair.projectId).includes(trimmedQuery)) {
      return true;
    }

    return false;
  });
}

/**
 * Filter projects by search query
 * Matches against project name
 *
 * @param projects - Array of project search objects
 * @param query - Search query string
 * @returns Filtered array of projects matching the query
 */
export function filterProjectsByQuery(
  projects: Array<{
    projectId: number;
    name: string;
    pairsCount: number;
    status: 'active' | 'archived';
  }>,
  query: string,
): Array<{
  projectId: number;
  name: string;
  pairsCount: number;
  status: 'active' | 'archived';
}> {
  if (!projects || projects.length === 0) {
    return [];
  }

  if (!query || !query.trim()) {
    return [];
  }

  const lowerQuery = query.toLowerCase().trim();

  return projects.filter(project => {
    // Match against project name
    if (project.name && project.name.toLowerCase().includes(lowerQuery)) {
      return true;
    }

    return false;
  });
}

/**
 * Generic filter function for any entity with a name or symbol field
 * Can be used for arbitrages, balancers, etc.
 *
 * @param entities - Array of entities to filter
 * @param query - Search query string
 * @param textFields - Array of field names to match against (default: ['name'])
 * @returns Filtered array of entities
 */
export function filterEntitiesByQuery<T extends Record<string, any>>(
  entities: T[],
  query: string,
  textFields: (keyof T)[] = ['name' as keyof T],
): T[] {
  if (!entities || entities.length === 0) {
    return [];
  }

  if (!query || !query.trim()) {
    return [];
  }

  const lowerQuery = query.toLowerCase().trim();

  return entities.filter(entity => {
    return textFields.some(field => {
      const value = entity[field];
      if (typeof value === 'string') {
        return value.toLowerCase().includes(lowerQuery);
      }
      return false;
    });
  });
}
